/**
 * @component ry-gradient-picker
 * @category  form
 * @summary   Gradient editor (ry-ui-only). Type toggle (solid / linear / radial), draggable color stops along a preview bar, per-stop color picker, optional angle (linear) or shape (radial). Parses CSS gradient strings on input and serializes back to spec syntax. Unlimited stops; click bar to add, drag to position, double-click to remove.
 *
 * @attribute value     string?              Initial gradient. Accepts `linear-gradient(...)`, `radial-gradient(...)`, or solid hex/rgb/hsl. Re-emitted in the same family.
 * @attribute output    'css' | 'tailwind' ?   default="css"   Output format for the `.value` getter (CSS gradient string or Tailwind class equivalents).
 * @attribute disabled  boolean   default=false   Non-interactive.
 *
 * @property  value         string   Read/write — full gradient CSS string.
 * @property  type          'solid' | 'linear' | 'radial'   Live gradient type. Setter switches the form.
 * @property  angle         number   0-360 angle for linear gradients (rotation degrees, normalized to [0, 360)).
 * @property  shape         'circle' | 'ellipse'   Shape for radial gradients.
 * @property  stops         GradientStop[]   Read-only — defensive copies of `{id, color, position}` for each stop.
 * @property  selectedStop  GradientStop | null   Read-only — currently focused stop (the one the inner color picker is editing), or null if none.
 * @property  disabled      boolean
 *
 * @event ry:input
 *        detail: { value: string, type: GradientType, angle: number, shape: RadialShape, stops: GradientStop[] }
 *        fires:  on every interaction step — stop drag, color picker change, angle/shape change.
 *
 * @event ry:beforechange
 *        detail: { value, type, angle, shape, stops, previous: string }
 *        fires:  before each commit — stop drag release, stop arrow keys / Delete, type or shape toggle, picker commit, output-CSS apply, `addStop()` / `removeStop()` — with `value`/`type`/`angle`/`shape`/`stops` the gradient ABOUT to be committed and `previous` the last committed CSS string. Not fired for the `ry:input` stream.
 *        cancelable: preventDefault() restores the last committed gradient (stops, type, shape, selection) and `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { value, type, angle, shape, stops }
 *        fires:  on commit (drag release, picker commit, type/shape/angle final value).
 *
 * @keyboard  Stop handles are keyboard-focusable; ArrowLeft / ArrowRight step the focused stop's position. Delete removes the focused stop (when more than one remains).
 *
 * @state     `data-ry-state` is not used (no open/closed semantics — picker is always inline).
 *
 * @content   None — `<ry-gradient-picker>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-gradient-picker[value][output?][disabled?]
 *     > div   [data-ry-target="preview"]    {full gradient preview area}
 *     > div   [data-ry-target="type-toggle"]    {solid / linear / radial segmented control}
 *     > div   [data-ry-target="bar"]    {gradient bar with draggable stop handles}
 *       > button   [data-ry-target="stop"]   data-stop-id  data-selected?   (one per stop)
 *     > div   [data-ry-target="controls"]    {angle (linear) or shape (radial) control}
 *     > ry-color-picker   [data-ry-target="picker"]   inline   (edits the selected stop)
 *     > div   [data-ry-target="output"]   {CSS string output + copy button}
 *
 * @gotcha  Gradient parsing is intentionally LENIENT: malformed input falls back to a default 2-stop gradient (red→blue) rather than throwing. To validate, check `el.value` after setting — if you set something unparseable, the returned value will be the fallback, not your input.
 *
 * @gotcha  Solid type internally is just a 2-stop gradient with identical colors. Switching from linear to solid preserves the first stop's color as the solid value; switching back to linear re-spreads stops to 0% / 100%.
 *
 * @gotcha  `el.stops` returns a NEW outer array but the stop OBJECTS may share state with internal storage. Pushing to / splicing the returned array does NOT affect the picker, but mutating `stops[i].color` is observable. To change stops cleanly, set `el.value = "..."` with the full gradient string — don't mutate stop objects in place.
 *
 * @gotcha  Drag-bar interactions need real layout — happy-dom can't simulate. Vitest covers programmatic + setter + parser paths. Cover drag with Playwright if needed.
 *
 * @gotcha  Angle is normalized to `[0, 360)` — setting `angle = 720` reads back as `0`; `angle = -45` reads back as `315`. The setter accepts any number and modulos it.
 *
 * @example linear
 * ```html
 * <ry-gradient-picker value="linear-gradient(90deg, #ff0000 0%, #0000ff 100%)"></ry-gradient-picker>
 * ```
 *
 * @example radial
 * ```html
 * <ry-gradient-picker value="radial-gradient(circle, #ff0000 0%, #00ff00 50%, #0000ff 100%)"></ry-gradient-picker>
 * ```
 *
 * @example solid
 * ```html
 * <ry-gradient-picker value="#3b82f6"></ry-gradient-picker>
 * ```
 *
 * @example disabled
 * ```html
 * <ry-gradient-picker value="linear-gradient(45deg, #ff0000, #0000ff)" disabled></ry-gradient-picker>
 * ```
 *
 * @tutorial mount-parses-linear-gradient
 * ```js
 * const el = mount('<ry-gradient-picker value="linear-gradient(90deg, #ff0000 0%, #0000ff 100%)"></ry-gradient-picker>');
 * await expectEqual(el.type, 'linear');
 * await expectEqual(el.angle, 90);
 * await expectEqual(el.stops.length, 2);
 * await expectEqual(el.stops[0].color, '#ff0000');
 * await expectEqual(el.stops[0].position, 0);
 * await expectEqual(el.stops[1].color, '#0000ff');
 * await expectEqual(el.stops[1].position, 100);
 * ```
 *
 * @tutorial mount-parses-radial-gradient
 * ```js
 * const el = mount('<ry-gradient-picker value="radial-gradient(circle, #ff0000 0%, #0000ff 100%)"></ry-gradient-picker>');
 * await expectEqual(el.type, 'radial');
 * await expectEqual(el.shape, 'circle');
 * await expectEqual(el.stops.length, 2);
 * ```
 *
 * @tutorial mount-parses-solid-color
 * ```js
 * // Wrong assumption: a plain hex is rejected. Solid colors are valid and
 * // internally stored as a 2-stop same-color gradient.
 * const el = mount('<ry-gradient-picker value="#3b82f6"></ry-gradient-picker>');
 * await expectEqual(el.type, 'solid');
 * ```
 *
 * @tutorial value-setter-replaces-gradient
 * ```js
 * const el = mount('<ry-gradient-picker value="linear-gradient(0deg, #ff0000, #0000ff)"></ry-gradient-picker>');
 * el.value = 'linear-gradient(45deg, #00ff00 0%, #ff00ff 100%)';
 * await expectEqual(el.angle, 45);
 * await expectEqual(el.stops[0].color, '#00ff00');
 * await expectEqual(el.stops[1].color, '#ff00ff');
 * ```
 *
 * @tutorial type-setter-switches-form
 * ```js
 * const el = mount('<ry-gradient-picker value="linear-gradient(0deg, #ff0000, #0000ff)"></ry-gradient-picker>');
 * await expectEqual(el.type, 'linear');
 * el.type = 'radial';
 * await expectEqual(el.type, 'radial');
 * el.type = 'solid';
 * await expectEqual(el.type, 'solid');
 * ```
 *
 * @tutorial angle-setter-normalizes-to-0-360
 * ```js
 * // Wrong assumption: angle clamps. It WRAPS via modulo so animation paths
 * // never "stick" at the edges.
 * const el = mount('<ry-gradient-picker value="linear-gradient(0deg, #ff0000, #0000ff)"></ry-gradient-picker>');
 * el.angle = 720;
 * await expectEqual(el.angle, 0, '720 mod 360 = 0');
 * el.angle = -45;
 * await expectEqual(el.angle, 315, '-45 normalizes to 315');
 * el.angle = 450;
 * await expectEqual(el.angle, 90);
 * ```
 *
 * @tutorial stops-getter-array-is-new-but-objects-may-share-state
 * ```js
 * // Wrong assumption: pushing to el.stops adds a stop. The outer array is
 * // new (push doesn't propagate) but the stop objects may share state with
 * // internal storage — don't mutate them in place.
 * const el = mount('<ry-gradient-picker value="linear-gradient(0deg, #ff0000, #0000ff)"></ry-gradient-picker>');
 * const stops = el.stops;
 * stops.push({ id: 'fake', color: '#fff', position: 50 });
 * await expectEqual(el.stops.length, 2, 'pushing to the returned array does not add a stop');
 * ```
 *
 * @tutorial invalid-input-falls-back-to-default-gradient
 * ```js
 * // Wrong assumption: setting an invalid gradient throws. It silently
 * // falls back to the parser's default (red → blue linear).
 * const el = mount('<ry-gradient-picker value="linear-gradient(0deg, #ff0000, #0000ff)"></ry-gradient-picker>');
 * el.value = 'not-a-gradient-at-all';
 * // Value returns the fallback, which is some valid gradient — verify it
 * // didn't throw + still has valid stops
 * await expectEqual(el.stops.length >= 2, true, 'still has stops');
 * await expectEqual(el.type === 'linear' || el.type === 'solid' || el.type === 'radial', true);
 * ```
 *
 * @tutorial disabled-attribute-survives
 * ```js
 * const el = mount('<ry-gradient-picker value="linear-gradient(0deg, #ff0000, #0000ff)" disabled></ry-gradient-picker>');
 * await expectEqual(el.disabled, true);
 * el.disabled = false;
 * await expectEqual(el.hasAttribute('disabled'), false);
 * el.disabled = true;
 * await expectEqual(el.hasAttribute('disabled'), true);
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange restores the last committed gradient
 * // and ry:change stays silent — e.g. "linear only in this theme".
 * const el = mount('<ry-gradient-picker value="linear-gradient(90deg, #ff0000 0%, #0000ff 100%)"></ry-gradient-picker>');
 * const radialBtn = el.querySelector('[data-ry-target="type-radial"]');
 * const changed = [];
 * el.addEventListener('ry:change', (e) => { if (e.target === el) changed.push(e.detail.type); });
 * el.addEventListener('ry:beforechange', (e) => {
 *   if (e.target === el && e.detail.type === 'radial') e.preventDefault();
 * }, { once: true });
 * radialBtn.click();
 * await expectEqual(el.type, 'linear', 'vetoed toggle keeps the linear gradient');
 * await expectEqual(el.value.startsWith('linear-gradient('), true, 'value restored');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * radialBtn.click();
 * await expectEqual(el.type, 'radial', 'without the veto, the toggle commits');
 * await expectEqual(changed[0], 'radial', 'ry:change fired with the new type');
 * ```
 *
 * @see ry-color-picker
 * @see ry-color-input
 */

import { RyElement } from '../core/ry-element.js';
import { getIcon } from '../core/ry-icons.js';
import type { RyColorPicker } from './ry-color-picker.js';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface GradientStop {
  id: string;
  color: string;
  position: number; // 0-100
}

export type GradientType = 'solid' | 'linear' | 'radial';
export type RadialShape = 'circle' | 'ellipse';

// ─────────────────────────────────────────────────────────────────────────────
// Minimal color parsing for interpolation (avoids touching ry-color-picker)
// ─────────────────────────────────────────────────────────────────────────────

interface SimpleRGB { r: number; g: number; b: number }

/** Everything a commit can change — captured as the baseline a veto restores. */
interface GradientSnapshot {
  value: string;
  type: GradientType;
  angle: number;
  shape: RadialShape;
  stops: GradientStop[];
  selectedStopId: string | null;
}

function parseColorToRgb(str: string): SimpleRGB | null {
  const s = str.trim().toLowerCase();

  // Hex
  if (s.startsWith('#')) {
    const hex = s.slice(1);
    if (hex.length === 3) {
      return {
        r: parseInt(hex[0]! + hex[0]!, 16),
        g: parseInt(hex[1]! + hex[1]!, 16),
        b: parseInt(hex[2]! + hex[2]!, 16),
      };
    }
    if (hex.length >= 6) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
      };
    }
    return null;
  }

  // rgb/rgba
  const rgbMatch = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(s);
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1]!, 10),
      g: parseInt(rgbMatch[2]!, 10),
      b: parseInt(rgbMatch[3]!, 10),
    };
  }

  // hsl/hsla → convert to RGB
  const hslMatch = /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%/.exec(s);
  if (hslMatch) {
    const h = parseFloat(hslMatch[1]!) / 360;
    const sat = parseFloat(hslMatch[2]!) / 100;
    const l = parseFloat(hslMatch[3]!) / 100;
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + sat) : l + sat - l * sat;
    const p = 2 * l - q;
    return {
      r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
      g: Math.round(hue2rgb(p, q, h) * 255),
      b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    };
  }

  return null;
}

function rgbToHex(rgb: SimpleRGB): string {
  const toHex = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

function lerpRgb(a: SimpleRGB, b: SimpleRGB, t: number): SimpleRGB {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_VALUE = 'linear-gradient(90deg, #000000 0%, #ffffff 100%)';
const DRAG_OFF_THRESHOLD = 50; // px vertical distance to trigger delete

export class RyGradientPicker extends RyElement {
  #type: GradientType = 'linear';
  #angle = 90;
  #shape: RadialShape = 'circle';
  #stops: GradientStop[] = [];
  #selectedStopId: string | null = null;
  #nextStopId = 0;

  #draggingStopId: string | null = null;
  #draggingOffBar = false;
  #barRect: DOMRect | null = null;
  #syncing = false;
  /** Last committed gradient — `previous` in ry:beforechange; a veto restores it. */
  #committed: GradientSnapshot | null = null;

  // Cached DOM refs
  #bar: HTMLElement | null = null;
  #picker: RyColorPicker | null = null;
  #outputInput: HTMLInputElement | null = null;

  static observedAttributes = ['value', 'disabled', 'output'] as const;

  setup(): void {
    this.#parseInitialValue();
    this.#buildMarkup();
    this.#bindEvents();
    this.#updateAll();
    this.#committed = this.#snapshot();
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Parsing / Serialization
  // ───────────────────────────────────────────────────────────────────────────

  #parseInitialValue(): void {
    const val = this.getAttribute('value') || DEFAULT_VALUE;
    if (!this.#parseGradient(val)) {
      this.#parseGradient(DEFAULT_VALUE);
    }
  }

  #parseGradient(css: string): boolean {
    const str = css.trim();

    // Solid color (not a gradient function)
    if (!str.startsWith('linear-gradient(') && !str.startsWith('radial-gradient(')) {
      const rgb = parseColorToRgb(str);
      if (!rgb) return false;
      this.#type = 'solid';
      this.#stops = [this.#createStop(str, 0), this.#createStop(str, 100)];
      this.#selectedStopId = this.#stops[0]!.id;
      return true;
    }

    let type: GradientType;
    let inner: string;

    if (str.startsWith('linear-gradient(')) {
      type = 'linear';
      inner = str.slice('linear-gradient('.length, -1);
    } else {
      type = 'radial';
      inner = str.slice('radial-gradient('.length, -1);
    }

    // Parse direction/shape from the start of inner
    let angle = 90;
    let shape: RadialShape = 'circle';
    let stopsStr = inner;

    if (type === 'linear') {
      // Match angle: "90deg" or "to right" etc.
      const angleMatch = /^([\d.]+)deg\s*,\s*/.exec(inner);
      if (angleMatch) {
        angle = parseFloat(angleMatch[1]!);
        stopsStr = inner.slice(angleMatch[0].length);
      } else {
        const toMatch = /^to\s+\w+(?:\s+\w+)?\s*,\s*/.exec(inner);
        if (toMatch) {
          // Convert "to right" → 90deg, etc. (simplified)
          const dir = toMatch[0].trim().replace(/,\s*$/, '');
          angle = this.#directionToAngle(dir);
          stopsStr = inner.slice(toMatch[0].length);
        }
      }
    } else {
      // Radial: match "circle" or "ellipse" at start
      const shapeMatch = /^(circle|ellipse)(?:\s+at\s+[^,]+)?\s*,\s*/.exec(inner);
      if (shapeMatch) {
        shape = shapeMatch[1] as RadialShape;
        stopsStr = inner.slice(shapeMatch[0].length);
      }
    }

    // Parse color stops
    const stops: GradientStop[] = [];
    // Match colors followed by optional percentage
    // This regex handles: #hex, rgb(...), rgba(...), hsl(...), hsla(...), named colors
    const stopRegex = /(#[a-fA-F0-9]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)|[a-z]+)\s+([\d.]+)%/g;
    let match: RegExpExecArray | null;

    while ((match = stopRegex.exec(stopsStr)) !== null) {
      stops.push(this.#createStop(match[1]!, parseFloat(match[2]!)));
    }

    if (stops.length < 2) return false;

    this.#type = type;
    this.#angle = angle;
    this.#shape = shape;
    this.#stops = stops;
    this.#selectedStopId = stops[0]!.id;
    return true;
  }

  #directionToAngle(dir: string): number {
    const map: Record<string, number> = {
      'to top': 0, 'to top right': 45, 'to right': 90,
      'to bottom right': 135, 'to bottom': 180, 'to bottom left': 225,
      'to left': 270, 'to top left': 315,
    };
    return map[dir] ?? 90;
  }

  #serializeGradient(): string {
    if (this.#type === 'solid') {
      return this.#stops[0]?.color ?? '#000000';
    }

    const stopsStr = this.#stops
      .map(s => `${s.color} ${Math.round(s.position)}%`)
      .join(', ');

    if (this.#type === 'linear') {
      return `linear-gradient(${Math.round(this.#angle)}deg, ${stopsStr})`;
    }
    return `radial-gradient(${this.#shape}, ${stopsStr})`;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Stop management
  // ───────────────────────────────────────────────────────────────────────────

  #createStop(color: string, position: number): GradientStop {
    return { id: `stop-${this.#nextStopId++}`, color, position };
  }

  /** CSS left value clamped so the stop handle stays within the bar */
  #stopLeft(position: number): string {
    const half = 'calc(var(--ry-gradient-picker-stop-size, 16px) / 2)';
    return `clamp(${half}, ${position}%, calc(100% - ${half}))`;
  }

  #addStop(color: string, position: number): void {
    const stop = this.#createStop(color, position);
    this.#stops.push(stop);
    this.#sortStops();
    this.#selectedStopId = stop.id;
    this.#renderStops();
    this.#updateAll();
  }

  #removeStop(id: string): boolean {
    if (this.#stops.length <= 2) return false;
    const idx = this.#stops.findIndex(s => s.id === id);
    if (idx === -1) return false;

    this.#stops.splice(idx, 1);

    // Select a neighbor if we removed the selected stop
    if (this.#selectedStopId === id) {
      this.#selectedStopId = this.#stops[Math.min(idx, this.#stops.length - 1)]?.id ?? null;
    }

    this.#renderStops();
    this.#updateAll();
    return true;
  }

  #selectStop(id: string): void {
    if (this.#selectedStopId === id) return;
    this.#selectedStopId = id;
    this.#updateStopSelection();
    this.#updatePicker();
  }

  #sortStops(): void {
    this.#stops.sort((a, b) => a.position - b.position);
  }

  #getSelectedStop(): GradientStop | null {
    return this.#stops.find(s => s.id === this.#selectedStopId) ?? null;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Markup
  // ───────────────────────────────────────────────────────────────────────────

  #buildMarkup(): void {
    const showOutput = this.hasAttribute('output');
    const copyIcon = getIcon('copy');
    const solidIcon = getIcon('gradient-solid');
    const linearIcon = getIcon('gradient-linear');
    const radialIcon = getIcon('gradient-radial');
    const circleIcon = getIcon('shape-circle');
    const ellipseIcon = getIcon('shape-ellipse');

    let outputHtml = '';
    if (showOutput) {
      outputHtml = `
      <div data-ry-target="output-row" class="ry-gradient-picker__output-row">
        <input data-ry-target="output" type="text" class="ry-gradient-picker__output" spellcheck="false" />
        <button data-ry-target="copy-btn" type="button" class="ry-gradient-picker__copy-btn">${copyIcon}</button>
      </div>`;
    }

    this.innerHTML = `
      <div data-ry-target="controls" class="ry-gradient-picker__controls">
        <div data-ry-target="type-toggle" class="ry-gradient-picker__type-toggle">
          <button data-ry-target="type-solid" type="button" class="ry-gradient-picker__type-btn">${solidIcon}</button>
          <button data-ry-target="type-linear" type="button" class="ry-gradient-picker__type-btn">${linearIcon}</button>
          <button data-ry-target="type-radial" type="button" class="ry-gradient-picker__type-btn">${radialIcon}</button>
        </div>
        <div data-ry-target="angle-control" class="ry-gradient-picker__angle-control">
          <ry-number-select data-ry-target="angle-select" min="0" max="360" step="1" value="${this.#angle}" wrap suffix="\u00b0" size="xs" arrows="none"></ry-number-select>
        </div>
        <div data-ry-target="shape-control" class="ry-gradient-picker__shape-control">
          <button data-ry-target="shape-circle" type="button" class="ry-gradient-picker__shape-btn">${circleIcon}</button>
          <button data-ry-target="shape-ellipse" type="button" class="ry-gradient-picker__shape-btn">${ellipseIcon}</button>
        </div>
      </div>
      <div data-ry-target="bar-container" class="ry-gradient-picker__bar-container">
        <div data-ry-target="bar" class="ry-gradient-picker__bar"></div>
      </div>
      <ry-color-picker data-ry-target="picker" inline opacity></ry-color-picker>
      ${outputHtml}
    `;

    // Cache refs
    this.#bar = this.$('[data-ry-target="bar"]');
    this.#picker = this.$('[data-ry-target="picker"]') as RyColorPicker;
    this.#outputInput = this.$('[data-ry-target="output"]') as HTMLInputElement | null;

    // Render stop handles on the bar
    this.#renderStops();
  }

  #renderStops(): void {
    if (!this.#bar) return;

    // Remove existing stop handles
    this.#bar.querySelectorAll('[data-ry-target="stop"]').forEach(el => el.remove());

    // Create stop handles
    for (const stop of this.#stops) {
      const el = document.createElement('div');
      el.setAttribute('data-ry-target', 'stop');
      el.setAttribute('data-stop-id', stop.id);
      el.classList.add('ry-gradient-picker__stop');
      el.tabIndex = 0;
      el.style.left = this.#stopLeft(stop.position);
      el.style.setProperty('--stop-color', stop.color);
      if (stop.id === this.#selectedStopId) {
        el.setAttribute('data-selected', '');
      }
      this.#bar.appendChild(el);
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Event binding
  // ───────────────────────────────────────────────────────────────────────────

  #bindEvents(): void {
    // Bar: click to add, pointerdown on stops, drag and release
    if (this.#bar) {
      this.on(this.#bar, 'pointerdown', this.#handleBarDown);
      this.on(this.#bar, 'pointermove', this.#handlePointerMove);
      this.on(this.#bar, 'pointerup', this.#handlePointerUp);
      this.on(this.#bar, 'lostpointercapture', this.#handlePointerUp);
    }

    // Type toggle
    const solidBtn = this.$('[data-ry-target="type-solid"]');
    const linearBtn = this.$('[data-ry-target="type-linear"]');
    const radialBtn = this.$('[data-ry-target="type-radial"]');
    if (solidBtn) this.on(solidBtn, 'click', () => this.#handleTypeToggle('solid'));
    if (linearBtn) this.on(linearBtn, 'click', () => this.#handleTypeToggle('linear'));
    if (radialBtn) this.on(radialBtn, 'click', () => this.#handleTypeToggle('radial'));

    // Angle select
    const angleSelect = this.$('[data-ry-target="angle-select"]');
    if (angleSelect) this.on(angleSelect, 'ry:input', this.#handleAngleInput);

    // Shape toggle
    const circleBtn = this.$('[data-ry-target="shape-circle"]');
    const ellipseBtn = this.$('[data-ry-target="shape-ellipse"]');
    if (circleBtn) this.on(circleBtn, 'click', () => this.#handleShapeToggle('circle'));
    if (ellipseBtn) this.on(ellipseBtn, 'click', () => this.#handleShapeToggle('ellipse'));

    // Color picker
    if (this.#picker) {
      this.on(this.#picker, 'ry:input', this.#handlePickerInput);
      this.on(this.#picker, 'ry:change', this.#handlePickerChange);
    }

    // Output input (editable CSS)
    if (this.#outputInput) {
      this.on(this.#outputInput, 'keydown', this.#handleOutputKeydown);
      this.on(this.#outputInput, 'blur', this.#handleOutputBlur);
    }

    // Copy button
    const copyBtn = this.$('[data-ry-target="copy-btn"]');
    if (copyBtn) this.on(copyBtn, 'click', this.#handleCopy);

    // Keyboard on bar (delegated from stop handles)
    if (this.#bar) {
      this.on(this.#bar, 'keydown', this.#handleStopKeydown);
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Bar / Stop interaction handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleBarDown = (e: PointerEvent): void => {
    if (this.disabled) return;

    const target = e.target as HTMLElement;
    const stopEl = target.closest('[data-ry-target="stop"]') as HTMLElement | null;

    if (stopEl) {
      // Start dragging existing stop
      const stopId = stopEl.getAttribute('data-stop-id');
      if (!stopId) return;
      e.preventDefault();
      this.#draggingStopId = stopId;
      this.#draggingOffBar = false;
      this.#barRect = this.#bar!.getBoundingClientRect();
      this.#selectStop(stopId);
      this.#bar!.setPointerCapture(e.pointerId);
      stopEl.focus();
    } else {
      // Click on empty bar → add stop and start dragging it
      e.preventDefault();
      if (!this.#bar) return;
      const rect = this.#bar.getBoundingClientRect();
      const position = Math.round(Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)));
      const color = this.#interpolateColorAt(position);
      this.#addStop(color, position);
      const newStopEl = this.#bar.querySelector(`[data-stop-id="${this.#selectedStopId}"]`) as HTMLElement | null;
      newStopEl?.focus();
      // Start dragging the new stop immediately
      this.#draggingStopId = this.#selectedStopId;
      this.#draggingOffBar = false;
      this.#barRect = rect;
      this.#bar.setPointerCapture(e.pointerId);
      this.#emitInput();
    }
  };

  #handlePointerMove = (e: PointerEvent): void => {
    if (!this.#draggingStopId || !this.#barRect) return;

    const stop = this.#stops.find(s => s.id === this.#draggingStopId);
    if (!stop) return;

    // Horizontal: position
    const x = Math.max(0, Math.min(1, (e.clientX - this.#barRect.left) / this.#barRect.width));
    stop.position = Math.round(x * 100);

    // Vertical: detect drag-off for deletion
    const barCenterY = this.#barRect.top + this.#barRect.height / 2;
    const yDist = Math.abs(e.clientY - barCenterY);
    this.#draggingOffBar = yDist > DRAG_OFF_THRESHOLD && this.#stops.length > 2;

    // Update the stop handle
    const stopEl = this.#bar?.querySelector(`[data-stop-id="${stop.id}"]`) as HTMLElement | null;
    if (stopEl) {
      stopEl.style.left = this.#stopLeft(stop.position);
      if (this.#draggingOffBar) {
        stopEl.style.opacity = '0.3';
        stopEl.style.transform = 'translate(-50%, -50%) scale(0.7)';
      } else {
        stopEl.style.opacity = '';
        stopEl.style.transform = '';
      }
    }

    this.#sortStops();
    this.#updateBar();
    this.#updatePreview();
    this.#updateOutput();
    this.#emitInput();
  };

  #handlePointerUp = (): void => {
    if (!this.#draggingStopId) return;

    if (this.#draggingOffBar) {
      this.#removeStop(this.#draggingStopId);
    }

    this.#draggingStopId = null;
    this.#draggingOffBar = false;
    this.#barRect = null;
    this.#syncValueAttribute();
    this.#commitChange();
  };

  #handleStopKeydown = (e: KeyboardEvent): void => {
    if (this.disabled) return;
    const target = e.target as HTMLElement;
    const stopEl = target.closest('[data-ry-target="stop"]') as HTMLElement | null;
    if (!stopEl) return;

    const stopId = stopEl.getAttribute('data-stop-id');
    if (!stopId) return;

    const stop = this.#stops.find(s => s.id === stopId);
    if (!stop) return;

    const step = e.shiftKey ? 10 : 1;
    let changed = false;

    switch (e.key) {
      case 'ArrowRight':
        stop.position = Math.min(100, stop.position + step);
        changed = true;
        break;
      case 'ArrowLeft':
        stop.position = Math.max(0, stop.position - step);
        changed = true;
        break;
      case 'Delete':
      case 'Backspace':
        if (this.#removeStop(stopId)) {
          this.#emitInput();
          this.#commitChange();
        }
        e.preventDefault();
        return;
    }

    if (changed) {
      e.preventDefault();
      this.#sortStops();
      this.#updateStopPositions();
      this.#updateBar();
      this.#updatePreview();
      this.#updateOutput();
      this.#syncValueAttribute();
      this.#emitInput();
      this.#commitChange();
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Type / Angle / Shape handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleTypeToggle(type: GradientType): void {
    if (this.#type === type) return;
    this.#type = type;
    this.setAttribute('data-ry-type', type);
    this.#updateControls();
    this.#updatePreview();
    this.#updateOutput();
    this.#syncValueAttribute();
    this.#emitInput();
    this.#commitChange();
  }

  #handleAngleInput = (e: Event): void => {
    const detail = (e as CustomEvent).detail as { value: number };
    this.#angle = detail.value;
    this.#updatePreview();
    this.#updateOutput();
    this.#syncValueAttribute();
    this.#emitInput();
    // Angle has no commit step of its own; it becomes part of the baseline here.
    this.#committed = this.#snapshot();
  };

  #handleShapeToggle(shape: RadialShape): void {
    if (this.#shape === shape) return;
    this.#shape = shape;
    this.#updateControls();
    this.#updateOutput();
    this.#syncValueAttribute();
    this.#emitInput();
    this.#commitChange();
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Color picker handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handlePickerInput = (): void => {
    const stop = this.#getSelectedStop();
    if (!stop || !this.#picker) return;

    stop.color = this.#picker.value;
    this.#updateStopColor(stop);
    this.#updateBar();
    this.#updatePreview();
    this.#updateOutput();
    this.#emitInput();
  };

  #handlePickerChange = (): void => {
    this.#syncValueAttribute();
    this.#commitChange();
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Copy handler
  // ───────────────────────────────────────────────────────────────────────────

  #handleCopy = (): void => {
    const text = this.#serializeGradient();
    navigator.clipboard.writeText(text).then(() => {
      const btn = this.$('[data-ry-target="copy-btn"]');
      if (btn) {
        btn.setAttribute('data-copied', '');
        setTimeout(() => btn.removeAttribute('data-copied'), 1500);
      }
    });
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Output (editable CSS) handlers
  // ───────────────────────────────────────────────────────────────────────────

  #handleOutputKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.#applyOutputValue();
    }
  };

  #handleOutputBlur = (): void => {
    this.#applyOutputValue();
  };

  #applyOutputValue(): void {
    if (!this.#outputInput) return;
    const val = this.#outputInput.value.trim();
    if (val && this.#parseGradient(val)) {
      this.#renderStops();
      this.#updateAll();
      this.#syncValueAttribute();
      this.#emitInput();
      this.#commitChange();
    } else {
      // Reset to current value on invalid input
      this.#updateOutput();
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Color interpolation
  // ───────────────────────────────────────────────────────────────────────────

  #interpolateColorAt(position: number): string {
    if (this.#stops.length === 0) return '#808080';
    if (this.#stops.length === 1) return this.#stops[0]!.color;

    const sorted = [...this.#stops].sort((a, b) => a.position - b.position);

    // Before first or after last
    if (position <= sorted[0]!.position) return sorted[0]!.color;
    if (position >= sorted[sorted.length - 1]!.position) return sorted[sorted.length - 1]!.color;

    // Find bracketing stops
    let left = sorted[0]!;
    let right = sorted[1]!;
    for (let i = 0; i < sorted.length - 1; i++) {
      if (sorted[i]!.position <= position && sorted[i + 1]!.position >= position) {
        left = sorted[i]!;
        right = sorted[i + 1]!;
        break;
      }
    }

    const range = right.position - left.position;
    if (range === 0) return left.color;
    const t = (position - left.position) / range;

    const rgbA = parseColorToRgb(left.color);
    const rgbB = parseColorToRgb(right.color);
    if (!rgbA || !rgbB) return left.color;

    return rgbToHex(lerpRgb(rgbA, rgbB, t));
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Update methods
  // ───────────────────────────────────────────────────────────────────────────

  #updateAll(): void {
    this.setAttribute('data-ry-type', this.#type);
    this.#updateControls();
    this.#updateBar();
    this.#updatePreview();
    this.#updateStopPositions();
    this.#updateStopSelection();
    this.#updatePicker();
    this.#updateOutput();
  }

  #updateControls(): void {
    // Type toggle active state
    const solidBtn = this.$('[data-ry-target="type-solid"]');
    const linearBtn = this.$('[data-ry-target="type-linear"]');
    const radialBtn = this.$('[data-ry-target="type-radial"]');
    if (solidBtn) solidBtn.toggleAttribute('data-active', this.#type === 'solid');
    if (linearBtn) linearBtn.toggleAttribute('data-active', this.#type === 'linear');
    if (radialBtn) radialBtn.toggleAttribute('data-active', this.#type === 'radial');

    // Shape toggle active state
    const circleBtn = this.$('[data-ry-target="shape-circle"]');
    const ellipseBtn = this.$('[data-ry-target="shape-ellipse"]');
    if (circleBtn) circleBtn.toggleAttribute('data-active', this.#shape === 'circle');
    if (ellipseBtn) ellipseBtn.toggleAttribute('data-active', this.#shape === 'ellipse');

    // Angle select sync
    const angleSelect = this.$('[data-ry-target="angle-select"]');
    if (angleSelect) angleSelect.setAttribute('value', String(this.#angle));
  }

  #updatePreview(): void {
    if (!this.#picker) return;
    const swatch = this.#picker.querySelector('[data-ry-target="preview-color"]') as HTMLElement | null;
    if (swatch) {
      swatch.style.backgroundImage = this.#type === 'solid' ? '' : this.#serializeGradient();
    }
  }

  #updateBar(): void {
    if (!this.#bar) return;
    const barStops = this.#stops
      .map(s => `${s.color} ${s.position}%`)
      .join(', ');
    this.#bar.style.backgroundImage = `linear-gradient(to right, ${barStops})`;
  }

  #updateStopPositions(): void {
    if (!this.#bar) return;
    for (const stop of this.#stops) {
      const el = this.#bar.querySelector(`[data-stop-id="${stop.id}"]`) as HTMLElement | null;
      if (el) {
        el.style.left = this.#stopLeft(stop.position);
      }
    }
  }

  #updateStopColor(stop: GradientStop): void {
    if (!this.#bar) return;
    const el = this.#bar.querySelector(`[data-stop-id="${stop.id}"]`) as HTMLElement | null;
    if (el) {
      el.style.setProperty('--stop-color', stop.color);
    }
  }

  #updateStopSelection(): void {
    if (!this.#bar) return;
    const allStops = this.#bar.querySelectorAll('[data-ry-target="stop"]');
    for (const el of allStops) {
      if (el.getAttribute('data-stop-id') === this.#selectedStopId) {
        el.setAttribute('data-selected', '');
      } else {
        el.removeAttribute('data-selected');
      }
    }
  }

  #updatePicker(): void {
    const stop = this.#getSelectedStop();
    if (!stop || !this.#picker) return;
    this.#picker.setColor(stop.color);
  }

  #updateOutput(): void {
    if (this.#outputInput && document.activeElement !== this.#outputInput) {
      this.#outputInput.value = this.#serializeGradient();
    }
  }

  #syncValueAttribute(): void {
    const val = this.#serializeGradient();
    if (this.getAttribute('value') !== val) {
      this.#syncing = true;
      this.setAttribute('value', val);
      this.#syncing = false;
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Events
  // ───────────────────────────────────────────────────────────────────────────

  #emitInput(): void {
    this.emit('input', {
      value: this.#serializeGradient(),
      type: this.#type,
      angle: this.#angle,
      shape: this.#shape,
      stops: this.stops,
    });
  }

  #emitChange(): void {
    this.emit('change', {
      value: this.#serializeGradient(),
      type: this.#type,
      angle: this.#angle,
      shape: this.#shape,
      stops: this.stops,
    });
  }

  #snapshot(): GradientSnapshot {
    return {
      value: this.#serializeGradient(),
      type: this.#type,
      angle: this.#angle,
      shape: this.#shape,
      stops: this.stops,
      selectedStopId: this.#selectedStopId,
    };
  }

  #restore(snap: GradientSnapshot): void {
    this.#type = snap.type;
    this.#angle = snap.angle;
    this.#shape = snap.shape;
    this.#stops = snap.stops.map(s => ({ ...s }));
    this.#selectedStopId = snap.selectedStopId;
    this.#renderStops();
    this.#updateAll();
    this.#syncValueAttribute();
  }

  /**
   * Commit choke point. Every commit path (drag release, keys, toggles, picker,
   * output CSS, addStop/removeStop) has already applied its mutation — the
   * before-event judges the result against the last committed gradient, and a
   * veto restores that snapshot (stop ids and selection included).
   */
  #commitChange(): boolean {
    const previous = this.#committed ?? this.#snapshot();
    if (!this.emit('beforechange', {
      value: this.#serializeGradient(),
      type: this.#type,
      angle: this.#angle,
      shape: this.#shape,
      stops: this.stops,
      previous: previous.value,
    })) {
      this.#restore(previous);
      return false;
    }
    this.#committed = this.#snapshot();
    this.#emitChange();
    return true;
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Attribute changed
  // ───────────────────────────────────────────────────────────────────────────

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue || this.#syncing) return;

    if (name === 'value' && this.#bar && newValue) {
      // External value change
      if (this.#parseGradient(newValue)) {
        this.#renderStops();
        this.#updateAll();
        this.#committed = this.#snapshot();
      }
    }
  }

  // ───────────────────────────────────────────────────────────────────────────
  // Public API
  // ───────────────────────────────────────────────────────────────────────────

  get value(): string {
    return this.#serializeGradient();
  }

  set value(val: string) {
    if (this.#parseGradient(val)) {
      this.#renderStops();
      this.#updateAll();
      this.#syncValueAttribute();
      this.#committed = this.#snapshot();
    }
  }

  get type(): GradientType {
    return this.#type;
  }

  set type(val: GradientType) {
    this.#handleTypeToggle(val);
  }

  get angle(): number {
    return this.#angle;
  }

  set angle(val: number) {
    this.#angle = ((val % 360) + 360) % 360;
    this.#updateControls();
    this.#updateOutput();
    this.#syncValueAttribute();
    this.#committed = this.#snapshot();
  }

  get shape(): RadialShape {
    return this.#shape;
  }

  set shape(val: RadialShape) {
    this.#handleShapeToggle(val);
  }

  get stops(): GradientStop[] {
    return this.#stops.map(s => ({ ...s }));
  }

  get selectedStop(): GradientStop | null {
    const stop = this.#getSelectedStop();
    return stop ? { ...stop } : null;
  }

  get disabled(): boolean {
    return this.hasAttribute('disabled');
  }

  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  addStop(color: string, position: number): void {
    this.#addStop(color, position);
    this.#syncValueAttribute();
    this.#emitInput();
    this.#commitChange();
  }

  removeStop(id: string): boolean {
    const removed = this.#removeStop(id);
    if (removed) {
      this.#updateOutput();
      this.#syncValueAttribute();
      // A vetoed commit restores the stop — report that as not removed.
      return this.#commitChange();
    }
    return removed;
  }
}

customElements.define('ry-gradient-picker', RyGradientPicker);
